import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator, Copy, Check, ShieldCheck, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

const MATERIALS = [
  { key: "platinum", label: "Platinum Cured Silicone" },
  { key: "peroxide", label: "Peroxide Cured Silicone" },
];
const TYPES = [
  { key: "solid", label: "Silicone Solid" },
  { key: "sponge", label: "Silicone Sponge" },
];

const PRODUCTS = [
  { key: "tube", label: "Tube / Hose", fields: [
      { key: "id", label: "Inner Diameter (ID)", unit: "mm" },
      { key: "od", label: "Outer Diameter (OD)", unit: "mm" }],
    area: (v) => (v.od > v.id ? (Math.PI * (v.od * v.od - v.id * v.id)) / 4 : 0),
    invalid: (v) => v.od > 0 && v.id > 0 && v.od <= v.id ? "OD must be greater than ID" : "" },
  { key: "braided", label: "Braided Hose", note: "Silicone material cost only — braid not included", fields: [
      { key: "id", label: "Inner Diameter (ID)", unit: "mm" },
      { key: "od", label: "Outer Diameter (OD)", unit: "mm" }],
    area: (v) => (v.od > v.id ? (Math.PI * (v.od * v.od - v.id * v.id)) / 4 : 0),
    invalid: (v) => v.od > 0 && v.id > 0 && v.od <= v.id ? "OD must be greater than ID" : "" },
  { key: "wire", label: "Wire Reinforced Hose", note: "Silicone material cost only — wire not included", fields: [
      { key: "id", label: "Inner Diameter (ID)", unit: "mm" },
      { key: "od", label: "Outer Diameter (OD)", unit: "mm" }],
    area: (v) => (v.od > v.id ? (Math.PI * (v.od * v.od - v.id * v.id)) / 4 : 0),
    invalid: (v) => v.od > 0 && v.id > 0 && v.od <= v.id ? "OD must be greater than ID" : "" },
  { key: "strip", label: "Strip", fields: [
      { key: "w", label: "Width", unit: "mm" },
      { key: "t", label: "Thickness", unit: "mm" }],
    area: (v) => v.w * v.t },
  { key: "cord", label: "Cord", fields: [
      { key: "d", label: "Diameter", unit: "mm" }],
    area: (v) => (Math.PI * v.d * v.d) / 4 },
  { key: "profile", label: "Profile", fields: [
      { key: "csa", label: "Cross Section Area", unit: "mm²" }],
    area: (v) => v.csa },
  { key: "sheet", label: "Sheet", fields: [
      { key: "w", label: "Width", unit: "mm" },
      { key: "t", label: "Thickness", unit: "mm" }],
    area: (v) => v.w * v.t },
];

const LS_KEY = "crm_rate_finder";
const loadPrefs = () => { try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; } };

const fmt = (n, dp = 2) => Number(n).toLocaleString("en-IN", { minimumFractionDigits: dp, maximumFractionDigits: dp });

export default function RateFinder() {
  const { user } = useAuth();
  const prefs = useMemo(loadPrefs, []);
  const [material, setMaterial] = useState(prefs.material || "platinum");
  const [mtype, setMtype] = useState(prefs.mtype || "solid");
  const [product, setProduct] = useState(prefs.product || "tube");
  const [dims, setDims] = useState({});
  const [config, setConfig] = useState(null);
  const [rateInput, setRateInput] = useState("");
  const [copied, setCopied] = useState(false);
  const [showDensity, setShowDensity] = useState(false);
  const [densityDraft, setDensityDraft] = useState({});

  const combo = `${material}_${mtype}`;
  const prod = PRODUCTS.find((p) => p.key === product);

  useEffect(() => {
    api.get("/rate-finder/config").then((r) => {
      setConfig(r.data);
      setDensityDraft(r.data.densities);
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    if (config) setRateInput(config.rates[combo] ? String(config.rates[combo]) : "");
  }, [config, combo]);

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify({ material, mtype, product }));
  }, [material, mtype, product]);

  const saveRate = async () => {
    const rate = parseFloat(rateInput) || 0;
    if (!config || config.rates[combo] === rate) return;
    try {
      const r = await api.put("/rate-finder/rate", { combo, rate });
      setConfig(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveDensities = async () => {
    try {
      const r = await api.put("/rate-finder/densities", { densities: densityDraft });
      setConfig(r.data);
      toast.success("Densities updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const vals = {};
  prod.fields.forEach((f) => { vals[f.key] = parseFloat(dims[`${product}_${f.key}`]) || 0; });
  const allFilled = prod.fields.every((f) => vals[f.key] > 0);
  const errMsg = prod.invalid ? prod.invalid(vals) : "";
  const density = config?.densities?.[combo] || 0;
  const rate = parseFloat(rateInput) || 0;

  const area = allFilled && !errMsg ? prod.area(vals) : 0;
  const volume = area * 1000;
  const weightKg = (volume * density) / 1000000;
  const refRate = weightKg * rate;
  const ready = area > 0 && rate > 0 && density > 0;

  const matLabel = MATERIALS.find((m) => m.key === material).label;
  const typeLabel = TYPES.find((t) => t.key === mtype).label;

  const copyResult = async () => {
    const dimsTxt = prod.fields.map((f) => `${f.label}: ${vals[f.key]}${f.unit}`).join(" · ");
    const txt = `Rate Finder — ${matLabel} · ${typeLabel} · ${prod.label}\n${dimsTxt}\nWeight/Meter: ${fmt(weightKg * 1000, 1)} g (${fmt(weightKg, 4)} kg)\nRaw Material Rate: ₹${fmt(rate)}/kg\nReference Rate: ₹${fmt(refRate)}/Meter`;
    try {
      await navigator.clipboard.writeText(txt);
      setCopied(true);
      toast.success("Result copied");
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Could not copy"); }
  };

  const pill = (active) =>
    `px-3.5 py-2 rounded-md text-sm border transition-colors duration-200 ${
      active ? "bg-[#3B82F6] text-white border-[#3B82F6] font-semibold shadow-sm"
             : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`;

  return (
    <div className="space-y-5" data-testid="rate-finder-page">
      <PageHeader subtitle="Internal Reference Tool" title="Rate Finder"
        right={<span className="text-xs text-slate-500 flex items-center gap-1.5"><Calculator size={14} /> Raw material cost per meter — reference only</span>} />

      <div className="grid lg:grid-cols-5 gap-5 items-start">
        {/* Left: inputs */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Material</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {MATERIALS.map((m) => (
                  <button key={m.key} className={pill(material === m.key)} onClick={() => setMaterial(m.key)}
                          data-testid={`rf-material-${m.key}`}>{m.label}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Material Type</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {TYPES.map((t) => (
                  <button key={t.key} className={pill(mtype === t.key)} onClick={() => setMtype(t.key)}
                          data-testid={`rf-type-${t.key}`}>{t.label}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Product</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {PRODUCTS.map((p) => (
                  <button key={p.key} className={pill(product === p.key)} onClick={() => setProduct(p.key)}
                          data-testid={`rf-product-${p.key}`}>{p.label}</button>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="grid sm:grid-cols-3 gap-3">
              {prod.fields.map((f) => (
                <div key={f.key}>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">{f.label}</Label>
                  <div className="relative mt-1.5">
                    <Input type="number" min="0" inputMode="decimal" value={dims[`${product}_${f.key}`] || ""}
                           onChange={(e) => setDims({ ...dims, [`${product}_${f.key}`]: e.target.value })}
                           className="pr-12" data-testid={`rf-dim-${f.key}`} />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">{f.unit}</span>
                  </div>
                </div>
              ))}
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Raw Material Rate</Label>
                <div className="relative mt-1.5">
                  <Input type="number" min="0" inputMode="decimal" value={rateInput}
                         onChange={(e) => setRateInput(e.target.value)} onBlur={saveRate}
                         className="pr-12" data-testid="rf-rate" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">₹/kg</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Saved per material combination</p>
              </div>
            </div>
            {errMsg && <p className="text-xs text-rose-600 mt-2" data-testid="rf-error">{errMsg}</p>}
            {prod.note && <p className="text-[11px] text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">{prod.note}</p>}
          </div>

          {user?.role === "admin" && (
            <div className="bg-white border border-slate-200 rounded-lg">
              <button className="w-full flex items-center justify-between px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-600"
                      onClick={() => setShowDensity((s) => !s)} data-testid="rf-density-toggle">
                <span className="flex items-center gap-1.5"><ShieldCheck size={13} className="text-emerald-600" /> Density Settings (Admin)</span>
                <ChevronDown size={14} className={`transition-transform duration-200 ${showDensity ? "rotate-180" : ""}`} />
              </button>
              {showDensity && (
                <div className="px-4 pb-4 space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    {MATERIALS.flatMap((m) => TYPES.map((t) => {
                      const c = `${m.key}_${t.key}`;
                      return (
                        <div key={c}>
                          <Label className="text-[10px] uppercase tracking-wider text-slate-500">{m.label} + {t.label}</Label>
                          <div className="relative mt-1">
                            <Input type="number" step="0.01" min="0" value={densityDraft[c] ?? ""}
                                   onChange={(e) => setDensityDraft({ ...densityDraft, [c]: e.target.value })}
                                   className="pr-14 h-9" data-testid={`rf-density-${c}`} />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">g/cm³</span>
                          </div>
                        </div>
                      );
                    }))}
                  </div>
                  <Button size="sm" onClick={saveDensities} className="bg-[#3B82F6] hover:bg-[#3B82F6]/90 text-white" data-testid="rf-density-save">Save Densities</Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: result card */}
        <div className="lg:col-span-2 lg:sticky lg:top-20">
          <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-white" data-testid="rf-result-card">
            <div className="bg-[#3B82F6] text-white px-5 py-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">Reference Rate</div>
              <div className="flex items-end gap-1.5 mt-1">
                <span className="text-4xl font-display font-extrabold tabular-nums tracking-tight" data-testid="rf-ref-rate">
                  {ready ? `₹${fmt(refRate)}` : "—"}
                </span>
                {ready && <span className="text-sm text-white/70 pb-1">/ Meter</span>}
              </div>
              {!ready && <div className="text-xs text-white/50 mt-1">Enter dimensions and rate to calculate</div>}
            </div>
            <div className="p-5 space-y-2.5 text-sm">
              {[
                ["Material", matLabel],
                ["Material Type", typeLabel],
                ["Product", prod.label],
                ["Weight per Meter", ready ? `${fmt(weightKg * 1000, 1)} g  (${fmt(weightKg, 4)} kg)` : "—"],
                ["Raw Material Rate", rate > 0 ? `₹${fmt(rate)} / kg` : "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-3 border-b border-dashed border-slate-100 pb-2 last:border-0 last:pb-0">
                  <span className="text-slate-500 text-xs uppercase tracking-wider">{k}</span>
                  <span className="font-medium text-slate-800 text-right tabular-nums" data-testid={`rf-out-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</span>
                </div>
              ))}
              <Button onClick={copyResult} disabled={!ready} variant="outline" className="w-full mt-2 gap-1.5" data-testid="rf-copy">
                {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />} {copied ? "Copied" : "Copy Result"}
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
            Raw material cost only — excludes processing, wastage, braid/wire and overheads. Not linked to quotations or costing.
          </p>
        </div>
      </div>
    </div>
  );
}
